import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Timer, CheckCircle2, XCircle } from 'lucide-react';
import { quickChallenges } from '../mock';
import BrainSnapshot from './BrainSnapshot';
import { toast } from '../hooks/use-toast';

const AUTO_ADVANCE_MS = 1200;

const QuickChallenge = ({ onOpenAuth, onOpenOnboarding }) => {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | correct | wrong | timeup
  const puzzle = quickChallenges[idx];
  const [seconds, setSeconds] = useState(puzzle.seconds);
  const [results, setResults] = useState([]);
  const [showSnapshot, setShowSnapshot] = useState(false);
  const advanceRef = useRef(null);

  useEffect(() => {
    setSelected(null);
    setStatus('idle');
    setSeconds(quickChallenges[idx].seconds);
  }, [idx]);

  // Timer visible only for the LAST TWO questions
  const timerVisible = idx >= Math.max(0, quickChallenges.length - 2);

  useEffect(() => {
    if (status !== 'idle' || showSnapshot) return;
    if (!timerVisible) return; // no timer on early questions
    if (seconds <= 0) {
      setStatus('timeup');
      return;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, status, showSnapshot, timerVisible]);

  const advance = (isCorrect) => {
    // Record result
    setResults((prev) => [
      ...prev.filter((r) => r.category !== puzzle.category),
      { category: puzzle.category, correct: !!isCorrect },
    ]);

    if (advanceRef.current) clearTimeout(advanceRef.current);
    advanceRef.current = setTimeout(() => {
      if (idx < quickChallenges.length - 1) {
        setIdx(idx + 1);
      } else {
        setShowSnapshot(true);
      }
    }, AUTO_ADVANCE_MS);
  };

  // when status changes to non-idle, kick off advance
  useEffect(() => {
    if (status === 'correct') advance(true);
    if (status === 'wrong')   advance(false);
    if (status === 'timeup')  advance(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => () => advanceRef.current && clearTimeout(advanceRef.current), []);

  const pct = useMemo(() => (seconds / puzzle.seconds) * 100, [seconds, puzzle.seconds]);

  const choose = (opt) => {
    if (status !== 'idle') return;
    setSelected(opt);
    setStatus(opt === puzzle.answer ? 'correct' : 'wrong');
  };

  const fmt = (s) => `00:${String(Math.max(s, 0)).padStart(2, '0')}`;

  const closeSnapshotRestart = () => {
    setShowSnapshot(false);
    setResults([]);
    setIdx(0);
  };

  const gotoSignup = () => {
    setShowSnapshot(false);
    if (onOpenOnboarding) onOpenOnboarding();
    else toast({ title: 'Sign up', description: 'Onboarding flow coming soon' });
  };
  const gotoLogin = () => {
    setShowSnapshot(false);
    if (onOpenAuth) onOpenAuth();
    else toast({ title: 'Log in', description: 'Login flow coming soon' });
  };

  return (
    <>
      <div className="relative rounded-2xl bg-[#3D2817]/90 border border-[#5a3d24] backdrop-blur-sm p-5 sm:p-7 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]">
        {/* Progress dots */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {quickChallenges.map((c, i) => (
              <div
                key={c.id}
                className={`h-1.5 rounded-full transition-all ${
                  i < idx ? 'w-4 bg-[#F4C430]' : i === idx ? 'w-8 bg-[#F4C430]' : 'w-4 bg-[#5a3d24]'
                }`}
              />
            ))}
            <span className="ml-2 text-[11px] text-[#B8A990] uppercase tracking-widest">
              {puzzle.category} · {idx + 1}/{quickChallenges.length}
            </span>
          </div>
          <TimerRing pct={pct} label={fmt(seconds)} visible={timerVisible} />
        </div>

        <h3 className="mt-4 sm:mt-6 text-[#F5EDE0] text-lg sm:text-xl font-serif">Try a quick challenge</h3>
        <p className="mt-2 text-2xl sm:text-3xl md:text-4xl text-[#F5EDE0] font-serif">{puzzle.prompt}</p>

        <div className="mt-4 sm:mt-5 grid grid-cols-2 gap-2.5 sm:gap-3">
          {puzzle.options.map((opt) => {
            const isSel = selected === opt;
            const isRight = status !== 'idle' && opt === puzzle.answer;
            const isWrongPick = status === 'wrong' && isSel;
            return (
              <button
                key={opt}
                onClick={() => choose(opt)}
                disabled={status !== 'idle'}
                className={`rounded-xl border h-12 sm:h-14 text-base sm:text-lg text-[#F5EDE0] transition-all
                  ${isRight ? 'bg-[#F4C430] text-[#2A1810] border-[#F4C430]' : ''}
                  ${isWrongPick ? 'border-red-400 text-red-300' : ''}
                  ${status === 'idle'
                    ? 'border-[#5a3d24] hover:border-[#F4C430] hover:bg-[#4a3221] active:scale-[0.98]'
                    : 'border-[#5a3d24]'}
                `}
              >
                {opt}
              </button>
            );
          })}
        </div>

        <div className="mt-3 sm:mt-4 min-h-[24px] text-xs sm:text-sm">
          {status === 'correct' && (
            <span className="inline-flex items-center gap-1.5 text-[#F4C430]">
              <CheckCircle2 size={16} /> Nice! Moving on…
            </span>
          )}
          {status === 'wrong' && (
            <span className="inline-flex items-center gap-1.5 text-red-300">
              <XCircle size={16} /> Not quite — next challenge coming up
            </span>
          )}
          {status === 'timeup' && (
            <span className="inline-flex items-center gap-1.5 text-[#E8B923]">
              <Timer size={16} /> Time is up — next challenge coming up
            </span>
          )}
          {status === 'idle' && (
            <span className="text-[#B8A990]">Instant feedback, real progress</span>
          )}
        </div>
      </div>

      <BrainSnapshot
        open={showSnapshot}
        onClose={closeSnapshotRestart}
        results={results}
        onSignup={gotoSignup}
        onLogin={gotoLogin}
      />
    </>
  );
};

const TimerRing = ({ pct, label, visible = true }) => {
  if (!visible) return <div style={{ width: 48, height: 48 }} />;
  const size = 48;
  const r = 20;
  const c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#4a3221" strokeWidth="3" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="#F4C430" strokeWidth="3" fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s linear' }}
        />
      </svg>
      <span className="absolute text-[10px] font-semibold text-[#F5EDE0]">{label}</span>
    </div>
  );
};

export default QuickChallenge;
